import React, { useState } from "react";
import WellPlate from "./components/WellPlate/WellPlate";
import MicroplateFocus from "./components/MicroplateFocus/MicroplateFocus";
import TabNavigation from "./components/TabNavigation/TabNavigation";
import WellSelection from "./components/WellSelection/WellSelection";
import DispenseSolution from "./components/DispenseSolution/DispenseSolution";
import { Button } from "@labxchange/buttons";
import { useDispatch, useSelector } from "react-redux";
import { onUpdateWellsContent } from "../../store";
import { SimManager } from "@learningblocks/simulation-engine";

import "./style.scss";
import { ExperimentOutcomesModal } from "@learningblocks/storybook-components/lib/DesignChapter/ExperimentOutcomesModal";

interface ContentProps {
  isDispense?: boolean;
  dispensibleWells?: WellPosition[];
  dispensibleSolution?: string;
  dispensibleVolume?: number;
  wellsContent?: Record<WellPosition, { [key: string]: number }>;
}
export type WellPosition = `${string}${number}`;
export type WellStatus = "empty" | "selected" | "filled" | "selected filled";

interface RootState {
  microplate?: {
    wellsColors?: Record<
      string,
      Record<string, string | number | { value: number }>
    >;
  };
}

export const Content = (props: ContentProps) => {
  const [activeTab, setActiveTab] = useState<"select" | "dispense">("select");
  const [selectedWells, setSelectedWells] = useState<WellPosition[]>([]);
  const [incorrectWells, setIncorrectWells] = useState<WellPosition[]>([]);
  const [wellSelectionError, setWellSelectionError] = useState<string>("");
  const [filledWells, setFilledWells] = useState<WellPosition[]>(
    Object.keys(props.wellsContent || {}) as WellPosition[]
  );
  const [wellsContent, setWellsContent] = useState<{
    [key: WellPosition]: { [key: string]: number };
  }>(props.wellsContent || {});
  const [playAnimation, setPlayAnimation] = useState<boolean>(false);
  const dispatch = useDispatch();
  const wellsColors = useSelector(
    (state: RootState) => state.microplate?.wellsColors
  );

  const handleTabChange = (tab: "select" | "dispense") => {
    setActiveTab(tab);
    setIncorrectWells([]);
  };

  const handleNextClick = () => {
    if (activeTab === "select") {
      const getIncorrectSelections = (
        expected?: WellPosition[],
        actual?: WellPosition[]
      ): WellPosition[] | undefined => {
        return actual?.filter((well) => !expected?.includes(well));
      };

      const incorrectWells = getIncorrectSelections(
        props.dispensibleWells,
        selectedWells
      );
      setIncorrectWells(incorrectWells || []);
      if (incorrectWells && incorrectWells.length > 0) {
        setWellSelectionError(getErrorMessage(incorrectWells));
        return;
      }
      if (
        props.dispensibleWells?.length !== selectedWells.length ||
        !props.dispensibleWells?.every((well) => selectedWells.includes(well))
      ) {
        setWellSelectionError(
          "You have not yet selected all the needed wells. Please select additional wells."
        );
        return;
      }
      SimManager.getInstance().setInputValue("sim_state", {
        SELECTED_WELLS_MICROPLATE: true,
      });
      setActiveTab("dispense");
    } else {
      setFilledWells([...filledWells, ...selectedWells]);
      setSelectedWells([]);
      setActiveTab("select");
    }
  };

  const removeSelectedWell = (position: WellPosition) => {
    setSelectedWells((prev) => prev.filter((well) => well !== position));
  };

  const getWellStatus = (position: WellPosition): WellStatus => {
    const isSelected = selectedWells.includes(position);
    const isFilled = filledWells.includes(position);

    if (isSelected && isFilled) return "selected filled";
    if (isSelected) return "selected";
    if (isFilled) return "filled";
    return "empty";
  };

  const getErrorMessage = (incorrectWells: WellPosition[]): string => {
    if (incorrectWells.length === 1) {
      return `Oops, we aren't adding liquid to <b>${incorrectWells[0]}</b> for this step.`;
    } else if (incorrectWells.length === 2) {
      return `Oops, we aren't adding liquid to <b>${incorrectWells[0]} and ${incorrectWells[1]}</b> for this step.`;
    } else if (incorrectWells.length === 3) {
      return `Oops, we aren't adding liquid to <b>${incorrectWells[0]}, ${incorrectWells[1]}, and ${incorrectWells[2]}</b> for this step.`;
    } else {
      return "Oops, we aren't adding liquid to some of those wells.";
    }
  };

  const handleWellClick = (position: WellPosition) => {
    if (props.isDispense) {
      if (activeTab === "select") {
        setSelectedWells((prev) => {
          if (prev.includes(position)) {
            return prev.filter((well) => well !== position);
          } else {
            return [...prev, position];
          }
        });
        setIncorrectWells([]);
      }
    }
  };

  const onPlungerPress = () => {
    setWellsContent((prevWellsContent) => {
      const updatedWellsContent = JSON.parse(JSON.stringify(prevWellsContent));

      props.dispensibleWells?.forEach((well) => {
        if (!updatedWellsContent[well]) {
          updatedWellsContent[well] = {};
        }
        if (
          props.dispensibleSolution &&
          !updatedWellsContent[well][props.dispensibleSolution]
        ) {
          updatedWellsContent[well][props.dispensibleSolution] = 0;
        }
        if (
          props.dispensibleSolution &&
          updatedWellsContent[well][props.dispensibleSolution] !==
            props.dispensibleVolume
        ) {
          updatedWellsContent[well][props.dispensibleSolution] +=
            props.dispensibleVolume;
        }
      });

      dispatch(onUpdateWellsContent(updatedWellsContent));

      return updatedWellsContent;
    });

    // Trigger animation - force a state change to ensure it plays every time, even when animation changes
    setPlayAnimation(false);
    setTimeout(() => {
      setPlayAnimation(true);
    }, 10);
  };

  return (
    <div className="microplate-focus-modal-controls">
      {props.isDispense ? (
        <div className="dispense-actions-card-container">
          <div className="info-card dispense-actions-card">
            <TabNavigation
              activeTab={activeTab}
              onTabChange={handleTabChange}
            />

            <div className="content-area">
              {activeTab === "select" ? (
                <WellSelection
                  selectedWells={selectedWells}
                  onRemoveWell={removeSelectedWell}
                  incorrectWells={incorrectWells}
                  wellSelectionError={wellSelectionError}
                />
              ) : (
                <DispenseSolution onPlungerPress={onPlungerPress} />
              )}
              {activeTab === "select" && (
                <div className="actions">
                  <Button
                    className="primary-button"
                    onClick={handleNextClick}
                    titleDescriptor={{
                      id: "Next",
                      defaultMessage: "Next",
                      description: "Next button",
                    }}
                    iconName={"chevron-right"}
                    disabled={selectedWells.length === 0}
                    textBeforeIcon={true}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="info-card">
          <h2>96-Well Microplate</h2>
          <p>Click on a well to see what it contains.</p>
        </div>
      )}
      {props.isDispense && activeTab !== "select" ? (
        <MicroplateFocus
          autoPlay={false}
          dispensibleWells={props.dispensibleWells || []}
          playAnimation={playAnimation}
          dispensibleSolution={props.dispensibleSolution}
        ></MicroplateFocus>
      ) : (
        <WellPlate
          handleWellClick={handleWellClick}
          getWellStatus={getWellStatus}
          isDispense={props.isDispense}
          wellsContent={wellsContent}
          wellsColors={wellsColors}
        ></WellPlate>
      )}
    </div>
  );
};
